import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  CalendarDays,
  CreditCard,
  GraduationCap,
  LineChart,
  Lock,
  Play,
  Sparkles,
  Star,
  Trophy,
  Users,
} from "lucide-react";
import { heroImageUrl } from "@/lib/rewards";

// ----- Reveal-on-scroll hook -----
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Initial-viewport elements: show immediately so the hero isn't blank
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
      className={`transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      } ${className}`}
    >
      {children}
    </div>
  );
}

function CountUp({ end, suffix = "" }) {
  const [n, setN] = useState(0);
  const [ref, visible] = useReveal();
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const dur = 1200;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setN(Math.floor(start + (end - start) * p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, end]);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

// ----- Sections -----
function Nav() {
  return (
    <header className="sticky top-0 z-30 bg-stone-50/80 backdrop-blur border-b border-stone-200">
      <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 font-bold text-stone-900"
          data-testid="landing-logo"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-amber-500 text-white">
            🔑
          </span>
          KKS
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-stone-700">
          <Link to="/courses" className="hover:text-stone-900">
            Browse courses
          </Link>
          <a href="#how" className="hover:text-stone-900">
            How it works
          </a>
          <a href="#faq" className="hover:text-stone-900">
            FAQ
          </a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button variant="ghost" size="sm" data-testid="landing-signin">
              Sign in
            </Button>
          </Link>
          <Link to="/signup">
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="landing-signup-nav"
            >
              Sign up free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* soft amber wash */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-50/60 via-stone-50 to-stone-50" />
      <div className="relative mx-auto max-w-6xl px-6 py-16 lg:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <Reveal>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium mb-4">
              <Sparkles className="h-3 w-3" /> AI-assisted · Indian curriculum
              friendly
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-tight">
              Knowledge is{" "}
              <span className="text-amber-600">Key to Success</span>.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-4 text-base md:text-lg text-stone-600 max-w-prose">
              Personalised live classes, AI-built lessons, homework that earns
              your child stars. Built with love for Indian families.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="landing-signup-hero"
                >
                  Sign up free <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <a href="#how">
                <Button variant="outline" size="lg" data-testid="landing-watch-sample">
                  <Play className="mr-1 h-4 w-4" /> See how it works
                </Button>
              </a>
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 grid grid-cols-3 sm:grid-cols-4 gap-3 text-sm text-stone-600 max-w-xl">
              <Strip label="Chapters" value={12} />
              <Strip label="Lessons / chapter" value={3} />
              <Strip label="AI-personalised" value={100} suffix="%" />
              <Strip label="Live classes" value={48} suffix="+" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={120} className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-amber-200/60 bg-white">
            <img
              src={heroImageUrl}
              alt="Happy students learning with KKS"
              className="w-full h-auto"
              loading="eager"
              data-testid="landing-hero-image"
            />
          </div>
          {/* floating star accents */}
          <Star className="absolute -top-4 -right-4 h-10 w-10 text-amber-400 drop-shadow animate-pulse" />
          <Trophy className="absolute -bottom-4 -left-4 h-10 w-10 text-amber-500 drop-shadow" />
        </Reveal>
      </div>
    </section>
  );
}

function Strip({ label, value, suffix }) {
  return (
    <div>
      <div className="text-2xl font-semibold text-stone-900">
        <CountUp end={value} suffix={suffix} />
      </div>
      <div className="text-xs text-stone-500">{label}</div>
    </div>
  );
}

function How() {
  const steps = [
    {
      icon: Users,
      title: "Get mapped to your teacher",
      desc: "Admin sets you up with the right teacher for your grade & subject.",
    },
    {
      icon: CreditCard,
      title: "Pay securely",
      desc: "Stripe or Razorpay — UPI/cards/EMI. Pay-in-full or monthly plans.",
    },
    {
      icon: CalendarDays,
      title: "Attend, learn, earn stars",
      desc: "Twice on weekdays, once on weekends. Earn rewards as you go.",
    },
  ];
  return (
    <section id="how" className="mx-auto max-w-6xl px-6 py-16">
      <Reveal>
        <h2 className="text-2xl md:text-3xl font-semibold mb-2 text-stone-900">
          How it works
        </h2>
        <p className="text-stone-600 mb-8">
          From signup to your first ⭐ in three easy steps.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-3 gap-4">
        {steps.map((s, i) => (
          <Reveal key={i} delay={80 * i}>
            <Card className="border-stone-200 h-full hover:shadow-md transition-shadow">
              <CardContent className="py-6 space-y-3">
                <div className="rounded-md bg-amber-50 p-2.5 w-10 h-10 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-amber-700" />
                </div>
                <div className="text-xs uppercase font-semibold text-amber-700">
                  Step {i + 1}
                </div>
                <div className="font-medium text-stone-900">{s.title}</div>
                <p className="text-sm text-stone-600">{s.desc}</p>
              </CardContent>
            </Card>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ForWho() {
  const cards = [
    {
      title: "For Students",
      icon: GraduationCap,
      desc: "Live classes, homework that's autosaved, MCQ tests auto-graded, stars + badges to celebrate every step.",
    },
    {
      title: "For Parents",
      icon: LineChart,
      desc: "Track your child's progress with a clear weekly view. Secure payments, transparent fees.",
    },
    {
      title: "For Teachers",
      icon: Sparkles,
      desc: "AI-built courses, drag-drop chapter editor, grading queue, attendance — your toolkit, in one place.",
    },
  ];
  return (
    <section className="bg-stone-100/60 border-y border-stone-200">
      <div className="mx-auto max-w-6xl px-6 py-16 grid sm:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <Reveal key={i} delay={80 * i}>
            <Card className="border-stone-200 bg-white h-full">
              <CardContent className="py-6 space-y-3">
                <c.icon className="h-5 w-5 text-amber-600" />
                <div className="font-medium text-stone-900">{c.title}</div>
                <p className="text-sm text-stone-600">{c.desc}</p>
              </CardContent>
            </Card>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function AiShowcase() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
      <Reveal>
        <h2 className="text-2xl md:text-3xl font-semibold text-stone-900 mb-3">
          AI builds the course. <br />
          You add the magic.
        </h2>
        <p className="text-stone-600 mb-4">
          Teachers tell us the topic, level and audience — our AI drafts a
          full 3-chapter course with lesson notes, examples, talking points,
          homework and tests. Editable, exportable, brochure-ready.
        </p>
        <Link to="/login">
          <Button variant="outline" data-testid="landing-try-teacher">
            Try the editor (teacher login)
          </Button>
        </Link>
      </Reveal>
      <Reveal delay={120}>
        <Card className="border-stone-200 shadow-lg">
          <CardContent className="py-4 space-y-2 text-xs font-mono text-stone-700">
            <div className="text-amber-700">// generated in 38s</div>
            <pre className="bg-stone-50 border border-stone-200 rounded p-3 leading-relaxed">
{`{
  "course_title": "Algebra Basics for Grade 8",
  "summary": "A grade-8 friendly intro to algebra…",
  "chapters": [
    { "title": "Variables and Expressions", … },
    { "title": "Solving Simple Equations", … },
    { "title": "Simplifying Expressions", … }
  ]
}`}
            </pre>
          </CardContent>
        </Card>
      </Reveal>
    </section>
  );
}

function GamificationTeaser() {
  const badges = [
    "🥇 First Steps",
    "🎯 Perfect Score",
    "🔥 On a Roll",
    "💎 Diamond Streak",
    "🎓 Chapter Champion",
    "🏆 Course Conqueror",
    "⭐ Star Student",
    "✋ Present!",
  ];
  return (
    <section className="bg-amber-50 border-y border-amber-200">
      <div className="mx-auto max-w-6xl px-6 py-16 text-center">
        <Reveal>
          <h2 className="text-2xl md:text-3xl font-semibold text-stone-900">
            Stars, badges, streaks, applause.
          </h2>
          <p className="text-stone-700 mt-2">
            Learning that <em>feels</em> like winning.
          </p>
        </Reveal>
        <Reveal delay={150}>
          <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
            {badges.map((b) => (
              <span
                key={b}
                className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-sm shadow-sm hover:-translate-y-0.5 transition-transform"
              >
                {b}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ScheduleTeaser() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
      <Reveal>
        <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
          <div className="text-xs uppercase text-stone-500 mb-2 font-semibold">
            This week
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div key={i} className="text-center text-stone-400">
                {d}
              </div>
            ))}
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className={`aspect-square rounded ${
                  i < 5 ? "bg-amber-100" : "bg-emerald-100"
                } flex items-center justify-center text-stone-700`}
              >
                {i < 5 ? "2" : "1"}
              </div>
            ))}
          </div>
          <div className="text-xs text-stone-500 mt-3">
            Weekdays: 2 classes/day · Weekend: 1 class/day
          </div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <h2 className="text-2xl md:text-3xl font-semibold text-stone-900 mb-2">
          A rhythm that fits real life.
        </h2>
        <p className="text-stone-600">
          Twice on weekdays, once on weekends. Live Jitsi classroom, calendar
          sync, automatic reminders.
        </p>
      </Reveal>
    </section>
  );
}

function Plans() {
  const plans = [
    { name: "Pay in full", note: "One-time, lock the best price." },
    { name: "Part payment", note: "Pay 50% upfront, 50% mid-course." },
    { name: "Monthly", note: "Spread across the course duration." },
  ];
  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <Reveal>
        <h2 className="text-2xl md:text-3xl font-semibold text-stone-900 mb-2">
          Flexible payment plans
        </h2>
        <p className="text-stone-600 mb-6">
          INR via Razorpay (UPI/cards/wallets). USD via Stripe.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-3 gap-4">
        {plans.map((p, i) => (
          <Reveal key={p.name} delay={80 * i}>
            <Card className="border-stone-200 h-full">
              <CardContent className="py-6">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-4 w-4 text-amber-600" />
                  <div className="font-medium text-stone-900">{p.name}</div>
                </div>
                <p className="text-sm text-stone-600">{p.note}</p>
              </CardContent>
            </Card>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    {
      name: "Priya Iyer",
      role: "Parent, Bengaluru",
      quote:
        "My daughter actually looks forward to math class now. The stars and streaks are addictive — in a good way.",
    },
    {
      name: "Arjun Sharma",
      role: "Student, Class 8",
      quote:
        "I got my Chapter Champion badge last week! Tests are tough but the explanations after submit really help.",
    },
    {
      name: "Meera Patel",
      role: "Teacher, ICSE",
      quote:
        "I built a 12-chapter course in an afternoon. Grading queue + attendance in one screen — magic.",
    },
  ];
  return (
    <section className="bg-stone-100/70 border-y border-stone-200">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <Reveal>
          <h2 className="text-2xl md:text-3xl font-semibold text-stone-900 mb-6">
            Loved across India
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-4">
          {items.map((t, i) => (
            <Reveal key={t.name} delay={80 * i}>
              <Card className="border-stone-200 bg-white h-full">
                <CardContent className="py-6">
                  <div className="flex text-amber-500 mb-2">
                    {[0, 1, 2, 3, 4].map((j) => (
                      <Star
                        key={j}
                        className="h-4 w-4 fill-amber-500"
                      />
                    ))}
                  </div>
                  <p className="text-sm text-stone-700">"{t.quote}"</p>
                  <div className="mt-3 text-xs">
                    <div className="font-medium text-stone-900">{t.name}</div>
                    <div className="text-stone-500">{t.role}</div>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    {
      q: "Is the AI safe for kids?",
      a: "The AI helps teachers build courses. Students never chat with the AI directly. All content is reviewed by the teacher before publishing.",
    },
    {
      q: "Who actually teaches my child?",
      a: "Real, vetted human teachers conduct every live class. AI is a tool for them, not a replacement.",
    },
    {
      q: "How is payment secured?",
      a: "Payments go through Stripe (USD) or Razorpay (INR) directly. We never store card details. All API keys are encrypted at rest with Fernet.",
    },
    {
      q: "What if my child misses a class?",
      a: "Classwork notes and homework remain accessible 24/7. Schedule a make-up class with your teacher anytime.",
    },
    {
      q: "Do you support CBSE / ICSE / State boards?",
      a: "Yes — teachers select the board context during course creation, and the AI tailors examples accordingly.",
    },
    {
      q: "How do I cancel?",
      a: "Email your admin or use the cancellation flow inside your dashboard. Refunds follow the plan terms.",
    },
  ];
  return (
    <section id="faq" className="mx-auto max-w-3xl px-6 py-16">
      <Reveal>
        <h2 className="text-2xl md:text-3xl font-semibold text-stone-900 mb-6 text-center">
          Frequently asked
        </h2>
      </Reveal>
      <Reveal>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger
                className="text-left text-sm"
                data-testid={`faq-trigger-${i}`}
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-stone-600">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-gradient-to-r from-amber-500 to-amber-600">
      <div className="mx-auto max-w-6xl px-6 py-14 text-center">
        <Reveal>
          <h2 className="text-2xl md:text-4xl font-semibold text-white">
            Start your child's journey today.
          </h2>
          <p className="text-amber-50 mt-2">
            First chapter free. Cancel anytime.
          </p>
          <Link to="/signup" className="inline-block mt-5">
            <Button
              size="lg"
              className="bg-white text-amber-700 hover:bg-stone-100"
              data-testid="landing-signup-cta"
            >
              Sign up free <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-stone-900 text-stone-400">
      <div className="mx-auto max-w-6xl px-6 py-10 grid sm:grid-cols-4 gap-6 text-sm">
        <div>
          <div className="text-white font-bold mb-2 flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-amber-500 text-white">
              🔑
            </span>
            KKS
          </div>
          <p className="text-xs text-stone-500">
            Knowledge is Key to Success.
          </p>
        </div>
        <FooterCol
          title="Product"
          links={[
            ["Courses", "/courses"],
            ["Sign in", "/login"],
            ["Sign up", "/signup"],
          ]}
        />
        <FooterCol
          title="Resources"
          links={[
            ["How it works", "#how"],
            ["FAQ", "#faq"],
          ]}
        />
        <FooterCol
          title="Legal"
          links={[
            ["Privacy", "#"],
            ["Terms", "#"],
          ]}
        />
      </div>
      <div className="border-t border-stone-800">
        <div className="mx-auto max-w-6xl px-6 py-4 text-xs text-stone-500">
          © {new Date().getFullYear()} KKS · Built with ❤️ in India
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-white text-sm font-semibold mb-2">{title}</div>
      <ul className="space-y-1">
        {links.map(([l, h]) => (
          <li key={l}>
            {h.startsWith("#") ? (
              <a href={h} className="hover:text-white">
                {l}
              </a>
            ) : (
              <Link to={h} className="hover:text-white">
                {l}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="landing">
      <Nav />
      <Hero />
      <How />
      <ForWho />
      <AiShowcase />
      <GamificationTeaser />
      <ScheduleTeaser />
      <Plans />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
